import React, { createContext, useContext, useRef, useEffect, ReactNode } from 'react';

interface AudioPlayerContextType {
  audio: HTMLAudioElement | null;
  isPlaying: boolean;
  setIsPlaying: (playing: boolean) => void;
  play: () => Promise<void>;
  pause: () => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  }
  return context;
};

interface AudioPlayerProviderProps {
  children: ReactNode;
  streamUrl: string;
}

export const AudioPlayerProvider: React.FC<AudioPlayerProviderProps> = ({ children, streamUrl }) => {
  // Singleton audio instance - created once and reused
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [audioReady, setAudioReady] = React.useState(false); // Track when audio is ready

  // Create audio element once (only if streamUrl is provided)
  useEffect(() => {
    if (!streamUrl || streamUrl.trim() === '') {
      // No URL configured, don't create audio element
      // Clear existing audio if URL was removed
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setIsPlaying(false);
        setAudioReady(false);
      }
      return;
    }

    // Only create if we don't have an audio element
    if (!audioRef.current) {
      const audio = new Audio(streamUrl);
      audio.crossOrigin = 'anonymous';
      audio.preload = 'none';
      audio.volume = 1.0; // Fixed at 100%
      
      // Handle play/pause events
      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);
      const handleEnded = () => setIsPlaying(false);
      
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('ended', handleEnded);
      
      // Silent error handling
      audio.addEventListener('error', (e) => {
        console.warn('Audio player error:', e);
        setIsPlaying(false);
        setAudioReady(false);
      });

      audioRef.current = audio;
      setAudioReady(true); // Mark audio as ready - this triggers re-render
    } else if (audioRef.current.src !== streamUrl) {
      // If URL changed, update the src
      audioRef.current.src = streamUrl;
      audioRef.current.load(); // Reload with new source
      setAudioReady(true); // Ensure ready state is set
    }

    return () => {
      // Don't destroy audio on unmount - keep it alive for persistence
      // Only cleanup on full page reload
    };
  }, [streamUrl]);

  const play = async () => {
    if (!audioRef.current) {
      console.warn('AudioPlayer: Cannot play - audio element is null');
      return;
    }
    
    try {
      await audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.warn('AudioPlayer: Failed to play audio:', error);
      setIsPlaying(false);
    }
  };

  const pause = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    setIsPlaying(false);
  };

  // Always provide a consistent context value structure
  // Don't use useMemo here as it can prevent audio ref updates from propagating
  return (
    <AudioPlayerContext.Provider
      value={{
        audio: audioRef.current,
        isPlaying,
        setIsPlaying,
        play,
        pause,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
};

